import React from "react";

import {
  mdiCog,
  mdiDownload,
  mdiFilmstrip,
  mdiMicrophone,
  mdiTelevisionBox,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { cn } from "@/src/lib/utils";

import OptionCheckbox from "./OptionCheckbox";
import StreamFormatCheckbox from "./StreamFormatCheckbox";
import COLORS from "../../../constants/colors";
import {
  AUDIO_FORMAT_LIST,
  VIDEO_FORMAT_LIST,
} from "../../../constants/encoding";
import Block from "../../block/Block";
import Input from "../../form/Input";
import { SegmentedField } from "../../form-fields/SegmentedField";

import type { QualitiesCost } from "./EncodingProfileEdit";
import type { AppState } from "../../../reducers";

interface EncodingProfileFormProps {
  qualitiesCost: QualitiesCost | null;
}

function EncodingProfileForm({
  qualitiesCost,
}: EncodingProfileFormProps): React.JSX.Element {
  const { t } = useTranslation("editor-encoding-profile");

  const { watch } = useFormContext();

  const hasFastOption = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options?.allow_fast,
  );

  const fastProfileOptionValue = watch("fast");

  const fastFramerateOptions = [
    { label: t("encodingProfileForm.fastFramerateOption.25fps"), value: "25" },
    { label: t("encodingProfileForm.fastFramerateOption.30fps"), value: "30" },
    { label: t("encodingProfileForm.fastFramerateOption.50fps"), value: "50" },
    { label: t("encodingProfileForm.fastFramerateOption.60fps"), value: "60" },
  ];

  const fastFramerateTitleContainerClassNames = cn("flex basis-2/3 flex-col", {
    "opacity-50": !fastProfileOptionValue,
  });

  return (
    <form className="pb-[0.78125rem relative flex flex-col gap-[0.78125rem]">
      <Block>
        <div className="mb-7.5 flex flex-col">
          <div className="mb-6 flex items-center gap-[0.46875rem]">
            <h2 className="text-foreground text-[1.171875rem] font-semibold">
              {t("encodingProfileForm.profileName")}
            </h2>
          </div>
          <Input
            isFullWidth
            isRequired
            className="text-foreground mb-0!"
            name="label"
          />
        </div>
        <div className="bg-border h-px" />
        {hasFastOption ? (
          <React.Fragment>
            <div className="my-7.5 flex flex-col gap-6">
              <h2 className="text-foreground text-[1.171875rem] font-semibold">
                {t("encodingProfileForm.linearDelivery")}
              </h2>
              <OptionCheckbox
                color="#10b981"
                description={t("encodingProfileForm.fastAssetDescription")}
                icon={mdiTelevisionBox}
                multiplier={qualitiesCost?.fastAsset}
                name="fast"
                subtitle={t("encodingProfileForm.fastAssetSubtitle")}
                title={t("encodingProfileForm.fastAsset")}
              >
                <div className="flex items-center">
                  <div className={fastFramerateTitleContainerClassNames}>
                    <div className="text-foreground font-semibold">
                      {t("encodingProfileForm.fastTargetFramerate")}
                    </div>
                    <div className="text-muted-foreground text-[0.78125rem] font-medium">
                      {t("encodingProfileForm.fastTargetFramerateDescription")}
                    </div>
                  </div>
                  <div className="flex basis-1/3">
                    <SegmentedField
                      disabled={!fastProfileOptionValue}
                      name="fastFramerate"
                      options={fastFramerateOptions}
                    />
                  </div>
                </div>
              </OptionCheckbox>
            </div>
            <div className="bg-border h-px" />
          </React.Fragment>
        ) : null}
        <div className="my-7.5 flex flex-col gap-6.25">
          <h2 className="text-foreground text-[1.171875rem] font-semibold">
            {t("encodingProfileForm.vodDelivery")}
          </h2>
          <div className="flex flex-col gap-[0.78125rem]">
            <div className="text-muted-foreground flex items-center gap-1.25 px-0.5">
              <Icon path={mdiFilmstrip} size={0.8} />
              <h3 className="text-[0.78125rem] font-semibold tracking-widest uppercase">
                {t("encodingProfileForm.videoStreams")}
              </h3>
            </div>
            <div className="flex flex-wrap gap-[0.78125rem]">
              {VIDEO_FORMAT_LIST.map((format) => (
                <StreamFormatCheckbox
                  key={format}
                  className="w-full shrink-0 grow-0 sm:w-[calc(50%-0.5rem)] lg:w-[calc(33%-0.6875rem)]"
                  multiplier={qualitiesCost?.video[format]}
                  name={`videoFormats.${format}`}
                  title={t(`encodingProfileForm.videoFormat.${format}`)}
                  description={t(
                    `encodingProfileForm.videoFormatDescription.${format}`,
                  )}
                  subtitle={t(
                    `encodingProfileForm.videoFormatSublabel.${format}`,
                  )}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-[0.78125rem]">
            <div className="text-muted-foreground flex items-center gap-1.25 px-0.5">
              <Icon path={mdiMicrophone} size={0.8} />
              <h3 className="text-[0.78125rem] font-semibold tracking-widest uppercase">
                {t("encodingProfileForm.audioStreams")}
              </h3>
            </div>
            <div className="flex flex-wrap gap-[0.78125rem]">
              {AUDIO_FORMAT_LIST.map((format) => (
                <StreamFormatCheckbox
                  key={format}
                  className="w-full shrink-0 grow-0 sm:w-[calc(50%-0.8rem)] lg:w-[calc(33%-1.1rem)]"
                  multiplier={qualitiesCost?.audio[format]}
                  name={`audioFormats.${format}`}
                  title={t(`encodingProfileForm.audioFormat.${format}`)}
                  description={t(
                    `encodingProfileForm.audioFormatDescription.${format}`,
                  )}
                  subtitle={t(
                    `encodingProfileForm.audioFormatSublabel.${format}`,
                  )}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-[0.78125rem]">
            <div className="text-muted-foreground flex items-center gap-1.25 px-0.5">
              <Icon path={mdiCog} size={0.8} />
              <h3 className="text-[0.78125rem] font-semibold tracking-widest uppercase">
                {t("encodingProfileForm.processingOptions")}
              </h3>
            </div>
            <OptionCheckbox
              color={COLORS.primary.DEFAULT}
              description={t("encodingProfileForm.downloadToGoDescription")}
              icon={mdiDownload}
              multiplier={qualitiesCost?.downloadToGo}
              name="downloadToGo"
              subtitle={t("encodingProfileForm.downloadToGoSubtitle")}
              title={t("encodingProfileForm.downloadToGo")}
            />
          </div>
        </div>
      </Block>
    </form>
  );
}

export default EncodingProfileForm;
